<template>
  <div id="app-root" :class="{ 'modal-open': activeModal }">

    <!-- ─── Navbar ─── -->
    <header class="navbar" :class="{ scrolled: isScrolled }">
      <div class="nav-inner">
        <a href="#inicio" class="nav-logo" @click.prevent="scrollToSection($event)">
          <img src="https://neliel.mx/img/logo_neliel.png" alt="Neliel Papelería" />
        </a>
        <nav class="nav-links" :class="{ open: navOpen }">
          <a href="#inicio"         @click.prevent="navScrollTo($event)">Inicio</a>
          <a href="#novedades" v-if="products.length > 0" @click.prevent="navScrollTo($event)">Novedades</a>
          <a href="#categorias"      @click.prevent="navScrollTo($event)">Categorías</a>
          <a href="#servicios"       @click.prevent="navScrollTo($event)">Servicios</a>
          <a href="#taller-creativo" @click.prevent="navScrollTo($event)">Taller Creativo</a>
          <a href="#contacto"        @click.prevent="navScrollTo($event)">Contacto</a>
        </nav>
        <button v-if="isDev" class="btn btn-outline btn-sm nav-cta-pay" @click="showModal('PaymentInfo')">
          <i class="fas fa-credit-card"></i> Pagar
        </button>
        <button v-if="isDev" class="btn btn-outline btn-sm nav-cta-pay nav-ripple-btn" @click="goToRecomendados">
          Artículos Recomendados
        </button>
        <a href="#contacto" class="btn btn-primary btn-sm nav-cta" @click.prevent="navScrollTo($event)">
          Contáctanos
        </a>
        <button class="nav-toggle" :class="{ open: navOpen }" @click="toggleNav" aria-label="Menú">
          <span></span><span></span><span></span>
        </button>
      </div>
    </header>

    <!-- ─── Hero ─── -->
    <section id="inicio" class="hero-section">
      <canvas ref="heroCanvas" class="hero-particles" aria-hidden="true"></canvas>
      <div class="container hero-grid">
        <div class="hero-text">
          <h1 class="hero-title">Tu papelería<br/><em>de confianza</em></h1>
          <p class="hero-desc">
            Materiales escolares, impresiones, pagos de servicios y novedades.
          </p>
          <div class="hero-actions">
            <a href="#servicios" class="btn btn-primary" @click.prevent="navScrollTo($event)">
              Ver Servicios <i class="fas fa-arrow-right"></i>
            </a>
            <a href="#contacto" class="btn btn-ghost" @click.prevent="navScrollTo($event)">Contáctanos</a>
          </div>
        </div>
        <div class="hero-visual">
          <div class="hero-card">
            <img class="hero-card-logo" src="https://neliel.mx/img/logo_neliel.png" alt="Neliel" />
            <p class="hero-card-tagline">Papelería · Novedades · Servicios</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Novedades ─── -->
    <section id="novedades" class="novedades-section" v-if="products.length > 0">
      <div class="container">
        <span class="label-chip"><i class="fas fa-sparkles"></i> Recién llegado</span>
        <h2 class="section-title">Novedades</h2>
        <p class="section-desc">Lo más reciente en nuestra tienda, pensado para ti.</p>
        <div class="product-grid">
          <div class="product-card" v-for="product in products" :key="product.id">
            <div class="product-thumb" :style="{ background: product.thumbBg }">
              <i :class="product.icon"></i>
            </div>
            <div class="product-body">
              <span class="product-badge" v-if="product.isNew">Nuevo</span>
              <h3>{{ product.name }}</h3>
              <div class="price" v-if="product.showPrice">
                {{ product.price }}
                <span class="old-price" v-if="product.oldPrice">{{ product.oldPrice }}</span>
              </div>
              <a href="#" class="btn btn-primary btn-sm">Ver más</a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Servicios ─── -->
    <Servicios />

    <!-- ─── Categorías ─── -->
    <section id="categorias" class="categorias-section">
      <div class="container">
        <span class="label-chip"><i class="fas fa-grip"></i> Explora</span>
        <h2 class="section-title">Categorías</h2>
        <p class="section-desc">Encuentra exactamente lo que buscas.</p>
        <div class="category-grid">
          <div class="category-card" v-for="category in categories" :key="category.id">
            <div class="cat-visual" :style="{ background: category.thumbBg }">
              <i :class="category.icon"></i>
            </div>
            <div class="cat-body">
              <div class="cat-name">{{ category.name }}</div>
              <div class="cat-sub">{{ category.sub }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Taller Creativo ─── -->
    <section id="taller-creativo" class="taller-section">
      <div class="container">
        <span class="label-chip"><i class="fas fa-wand-magic"></i> Herramientas gratuitas</span>
        <h2 class="section-title">Taller Creativo</h2>
        <p class="section-desc">Crea materiales escolares y de oficina de forma fácil y rápida, directo desde tu navegador.</p>
        <div class="taller-carousel" @mouseenter="stopTallerAutoplay" @mouseleave="startTallerAutoplay"
          @touchstart.passive="stopTallerAutoplay" @touchend.passive="startTallerAutoplay">
          <button v-if="tallerCards.length > 3" class="taller-nav taller-nav--prev" @click="tallerPrev" aria-label="Anterior">
            <i class="fas fa-chevron-left"></i>
          </button>
          <transition-group :name="'taller-slide-' + tallerDirection" tag="div" class="taller-grid">
            <div v-for="card in visibleTallerCards" :key="card.id"
              class="taller-card"
              :class="{ 'taller-card--active': !!card.route, 'taller-card--center': card.pos === 'center' }"
              :style="{ cursor: card.route ? 'pointer' : 'default' }"
              @click="tallerCardClick(card)">
              <div class="taller-card-banner" :class="'taller-card-banner--' + card.banner">
                <i class="taller-card-banner-icon" :class="card.icon"></i>
                <div class="taller-card-deco-a"></div>
                <div class="taller-card-deco-b"></div>
              </div>
              <div class="taller-card-content">
                <div class="taller-card-badge" :class="{ 'taller-card-badge--new': card.badgeNew }">{{ card.badge }}</div>
                <h3>{{ card.name }}</h3>
                <p>{{ card.desc }}</p>
                <button v-if="card.route" class="btn btn-primary btn-sm" style="margin-top:16px;pointer-events:none">Abrir herramienta <i class="fas fa-arrow-right"></i></button>
                <button v-else class="btn btn-outline btn-sm" style="margin-top:16px;pointer-events:none" disabled>Muy pronto <i class="fas fa-clock"></i></button>
              </div>
            </div>
          </transition-group>
          <button v-if="tallerCards.length > 3" class="taller-nav taller-nav--next" @click="tallerNext" aria-label="Siguiente">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        <div class="taller-dots">
          <button v-for="(c, i) in tallerCards" :key="c.id"
            class="taller-dot" :class="{ 'taller-dot--active': i === tallerIndex }"
            @click="tallerGoTo(i, $event)" :aria-label="'Ir a ' + c.name"></button>
        </div>
      </div>
    </section>

    <!-- ─── Contacto ─── -->
    <section id="contacto" class="contacto-section">
      <div class="container">
        <span class="label-chip"><i class="fas fa-envelope"></i> Escríbenos</span>
        <h2 class="section-title">Contáctanos</h2>
        <p class="section-desc">Estamos para ayudarte. Escríbenos o visítanos en Puerto Vallarta.</p>
        <div class="contact-grid">
          <div class="contact-side">
            <h3>Información de contacto</h3>
            <a class="contact-item" href="https://www.google.com/maps/dir//Oaxaca+147,+Las+Mojoneras,+48290+Puerto+Vallarta,+Jal." target="_blank" rel="noopener noreferrer" @click="trackEvent('maps_click')">
              <div class="contact-icon"><i class="fas fa-location-dot"></i></div>
              <div class="contact-text">
                <strong>Dirección</strong>
                <span>Oaxaca #147 Col. Las Mojoneras, Puerto Vallarta, Jal.</span>
              </div>
            </a>
            <a class="contact-item" href="https://wa.me/523221472806" target="_blank" rel="noopener" @click="trackEvent('whatsapp_click', { location: 'contact' })">
              <div class="contact-icon"><i class="fab fa-whatsapp"></i></div>
              <div class="contact-text">
                <strong>WhatsApp</strong>
                <span>+52 322 147 2806</span>
              </div>
            </a>
            <a class="contact-item" href="mailto:contacto@neliel.mx" @click="trackEvent('email_click', { location: 'contact' })">
              <div class="contact-icon"><i class="fas fa-envelope"></i></div>
              <div class="contact-text">
                <strong>Correo electrónico</strong>
                <span>contacto@neliel.mx</span>
              </div>
            </a>
            <div class="contact-item">
              <div class="contact-icon"><i class="fas fa-clock"></i></div>
              <div class="contact-text">
                <strong>Horario</strong>
                <span>Lunes a Sábado · 9:00 – 19:00<br/>Domingo · 11:00 – 17:00</span>
              </div>
            </div>
          </div>
          <div class="form-card">
            <h4>Envíanos un mensaje</h4>
            <form id="main-contact-form" @submit.prevent="validateForm">
              <div class="form-row">
                <div class="form-group">
                  <label for="f-name">Nombre</label>
                  <input type="text" id="f-name" v-model="form.name" placeholder="Tu nombre" required />
                </div>
                <div class="form-group">
                  <label for="f-email">Correo electrónico</label>
                  <input type="email" id="f-email" v-model="form.email" placeholder="tu@correo.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="f-phone">Teléfono</label>
                <input type="tel" id="f-phone" v-model="form.phone" placeholder="+52 322 000 0000" required />
              </div>
              <div class="form-group">
                <label for="f-message">Mensaje</label>
                <textarea id="f-message" v-model="form.message" placeholder="¿En qué podemos ayudarte?" required></textarea>
              </div>
              <div class="captcha-row">
                <div class="captcha-code">{{ captchaText }}</div>
                <button type="button" class="refresh-btn" @click="generateCaptcha" aria-label="Refrescar captcha">
                  <i class="fas fa-sync-alt"></i>
                </button>
                <div class="form-group captcha-field">
                  <label for="f-captcha">Código de verificación</label>
                  <input type="text" id="f-captcha" v-model="form.captchaInput" placeholder="Escribe el código" required />
                </div>
              </div>
              <p class="form-note">Al enviar aceptas nuestra <a href="#" @click.prevent="showModal('PrivacyPolicy')">política de privacidad</a>.</p>
              <button type="submit" class="btn btn-primary btn-block">
                Enviar mensaje <i class="fas fa-paper-plane"></i>
              </button>
              <p class="form-status" :class="formStatus.type" v-if="formStatus.message">{{ formStatus.message }}</p>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Footer ─── -->
    <footer class="footer-section">
      <div class="container">
        <div class="footer-grid">
          <div class="footer-brand">
            <img src="https://neliel.mx/img/logo_neliel-naranja.png" alt="Neliel Papelería" class="footer-logo" />
            <p>Tu papelería de confianza en Puerto Vallarta. Materiales, impresiones, pagos de servicios y mucho más.</p>
            <div class="social-links">
              <a href="https://www.facebook.com/nelielmx" aria-label="Facebook" target="_blank" rel="noopener" @click="trackEvent('social_click', { platform: 'facebook' })">
                <i class="fab fa-facebook-f"></i>
              </a>
              <a href="https://www.instagram.com/nelielmx/" aria-label="Instagram" target="_blank" rel="noopener" @click="trackEvent('social_click', { platform: 'instagram' })">
                <i class="fab fa-instagram"></i>
              </a>
            </div>
          </div>
          <div class="footer-col">
            <h4>Navegación</h4>
            <ul>
              <li><a href="#inicio"         @click.prevent="navScrollTo($event)">Inicio</a></li>
              <li><a href="#novedades"       @click.prevent="navScrollTo($event)">Novedades</a></li>
              <li><a href="#categorias"      @click.prevent="navScrollTo($event)">Categorías</a></li>
              <li><a href="#servicios"       @click.prevent="navScrollTo($event)">Servicios</a></li>
              <li><a href="#contacto"        @click.prevent="navScrollTo($event)">Contacto</a></li>
            </ul>
          </div>
          <div class="footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="#" @click.prevent="showModal('PrivacyPolicy')">Política de Privacidad</a></li>
              <li><a href="#" @click.prevent="showModal('FAQ')">Preguntas Frecuentes</a></li>
            </ul>
          </div>
          <div class="footer-col">
            <h4>Contacto</h4>
            <p><i class="fas fa-envelope"></i> <a href="mailto:contacto@neliel.mx" @click="trackEvent('email_click', { location: 'footer' })">contacto@neliel.mx</a></p>
            <p><i class="fas fa-phone"></i> <a href="https://wa.me/523221472806" target="_blank" rel="noopener" @click="trackEvent('whatsapp_click', { location: 'footer' })">+52 322 147 2806</a></p>
            <p><i class="fas fa-location-dot"></i> <a href="https://www.google.com/maps/dir//Oaxaca+147,+Las+Mojoneras,+48290+Puerto+Vallarta,+Jal." target="_blank" rel="noopener">Puerto Vallarta, Jalisco</a></p>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; {{ currentYear }} Neliel Papelería y Novedades. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>

    <!-- WhatsApp FAB -->
    <a href="https://wa.me/523221472806?text=Hola" class="whatsapp-float-btn" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" @click="trackEvent('whatsapp_click', { location: 'fab' })">
      <i class="fab fa-whatsapp"></i>
    </a>
  </div>

  <!-- ─── Modals ─── -->
  <div class="modal-overlay" v-if="activeModal" @click="closeModal">
    <PrivacyPolicy  v-if="activeModal === 'PrivacyPolicy'"  @close="closeModal" />
    <FAQ            v-if="activeModal === 'FAQ'"            @close="closeModal" />
    <PaymentInfo    v-if="activeModal === 'PaymentInfo'"    @close="closeModal" />
  </div>

</template>

<script>
import FAQ from '../components/FAQ.vue';
import Servicios from '../components/Servicios.vue';
import PrivacyPolicy from '../components/PrivacyPolicy.vue';
import PaymentInfo from '../components/PaymentInfo.vue';
import { trackEvent } from '../utils/analytics';

export default {
  name: 'HomePage',
  components: { FAQ, Servicios, PrivacyPolicy, PaymentInfo },
  data() {
    return {
      activeModal: null,
      isScrolled: false,
      navOpen: false,
      products: [
        /* Uncomment when ready:
        { id: 1, name: 'Cuaderno Profesional A4', price: '$45.00', oldPrice: '$55.00', showPrice: true, isNew: true, icon: 'fas fa-book', thumbBg: 'linear-gradient(145deg, #f0c2b2, #f6dfa4)' },
        */
      ],
      categories: [
        { id: 1, name: 'Papelería Escolar',  sub: 'Cuadernos, colores, útiles',    icon: 'fas fa-book',     thumbBg: 'linear-gradient(145deg, #f0c2b2, #f6dfa4)' },
        { id: 2, name: 'Impresiones',         sub: 'Documentos, copias y más',   icon: 'fas fa-print',    thumbBg: 'linear-gradient(145deg, #f6dfa4, #fef9ec)' },
        { id: 3, name: 'Arte & Manualidades', sub: 'Pinturas, stickers',     icon: 'fas fa-palette',  thumbBg: 'linear-gradient(145deg, #c8edf8, #f0c2b2)' },
        { id: 4, name: 'Novedades',           sub: 'Artículos nuevos y temporada',  icon: 'fas fa-star',     thumbBg: 'linear-gradient(145deg, #e8f8c8, #f6dfa4)' },
      ],
      form: { name: '', email: '', phone: '', message: '', captchaInput: '' },
      captchaText: '',
      formStatus: { message: '', type: '' },
      tallerCards: [
        { id: 'etiquetas',  name: 'Etiquetas Escolares',   desc: 'Diseña e imprime etiquetas personalizadas para útiles, cuadernos y más.',                                                  icon: 'fas fa-tag',        banner: 'peach', badge: 'Nuevo',        badgeNew: true,  route: '/etiquetas'  },
        /* Oculto por ahora — descomentar para volver a publicar la tarjeta:
        { id: 'qr',         name: 'Generador de QR',       desc: 'Crea códigos QR personalizados al instante: para tu negocio, redes sociales, menús, links o cualquier uso.',                icon: 'fas fa-qrcode',     banner: 'teal',  badge: 'Nuevo',        badgeNew: true,  route: '/qr'         },
        */
        { id: 'plantillas', name: 'Plantillas Imprimibles',desc: 'Diseña hojas de trabajo, agendas y cuadernos con líneas, cuadrícula o puntos. Imprime al instante.',                        icon: 'fas fa-file-lines', banner: 'coral', badge: 'Nuevo',        badgeNew: true,  route: '/plantillas' },
        { id: 'soon',       name: 'Más Herramientas',      desc: 'Estamos preparando nuevas herramientas creativas: calendarios personalizados, tarjetas de presentación, invitaciones y diplomas. ¡Mantente atento!', icon: 'fas fa-rocket', banner: 'peach', badge: 'Próximamente', badgeNew: false, route: null          },
      ],
      tallerIndex: 1,
      tallerDirection: 'next',
    };
  },
  computed: {
    currentYear() { return new Date().getFullYear(); },
    isDev() { return process.env.NODE_ENV === 'development'; },
    visibleTallerCards() {
      const n = this.tallerCards.length;
      const idx = this.tallerIndex;
      return [
        { ...this.tallerCards[(idx - 1 + n) % n], pos: 'left' },
        { ...this.tallerCards[idx],               pos: 'center' },
        { ...this.tallerCards[(idx + 1) % n],     pos: 'right' },
      ];
    },
  },
  methods: {
    trackEvent,
    goToRecomendados(e) {
      const btn  = e.currentTarget
      const rect = btn.getBoundingClientRect()
      const size = Math.max(rect.width, rect.height) * 2
      const x    = e.clientX - rect.left - size / 2
      const y    = e.clientY - rect.top  - size / 2
      const ripple = document.createElement('span')
      ripple.className = 'ripple-wave'
      ripple.style.cssText = `width:${size}px;height:${size}px;left:${x}px;top:${y}px`
      btn.appendChild(ripple)
      ripple.addEventListener('animationend', () => ripple.remove())
      setTimeout(() => this.$router.push('/ofertas-recomendadas'), 350)
    },
    handleScroll() { this.isScrolled = window.scrollY > 50; },
    toggleNav()    { this.navOpen = !this.navOpen; },
    tallerPrev(e)  { this.pulseClick(e); this.tallerDirection = 'prev'; const n = this.tallerCards.length; this.tallerIndex = (this.tallerIndex - 1 + n) % n; },
    tallerNext(e)  { this.pulseClick(e); this.tallerDirection = 'next'; const n = this.tallerCards.length; this.tallerIndex = (this.tallerIndex + 1) % n; },
    tallerGoTo(i, e) { this.pulseClick(e); if (i !== this.tallerIndex) this.tallerDirection = i > this.tallerIndex ? 'next' : 'prev'; this.tallerIndex = i; this.startTallerAutoplay(); },
    startTallerAutoplay() {
      this.stopTallerAutoplay();
      if (this.tallerCards.length <= 3) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this._tallerTimer = setInterval(() => {
        this.tallerDirection = 'next';
        this.tallerIndex = (this.tallerIndex + 1) % this.tallerCards.length;
      }, 4500);
    },
    stopTallerAutoplay() {
      if (this._tallerTimer) { clearInterval(this._tallerTimer); this._tallerTimer = null; }
    },
    tallerCardClick(card) {
      if (card.route) {
        trackEvent('taller_tool_open', { tool: card.name });
        this.$router.push(card.route);
      }
    },
    pulseClick(e) {
      const el = e && e.currentTarget;
      if (!el) return;
      el.classList.remove('is-pulsing');
      void el.offsetWidth;
      el.classList.add('is-pulsing');
    },
    showModal(modalComponent) {
      trackEvent('modal_open', { modal: modalComponent });
      this.activeModal = modalComponent;
    },
    closeModal() {
      this.activeModal = null;
      if (this.$route.path === '/pago') this.$router.push('/');
    },
    scrollToSection(event) {
      const href = event.currentTarget.getAttribute('href');
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
    navScrollTo(event) {
      this.navOpen = false;
      const section = event.currentTarget.getAttribute('href').replace('#', '');
      trackEvent('nav_click', { section });
      this.scrollToSection(event);
    },
    generateCaptcha() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
      let result = '';
      for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
      this.captchaText = result;
      this.form.captchaInput = '';
    },
    async validateForm() {
      if (this.form.captchaInput !== this.captchaText) {
        this.formStatus = { message: 'El código de verificación es incorrecto. Intenta de nuevo.', type: 'error' };
        this.generateCaptcha();
        return;
      }
      this.formStatus = { message: 'Enviando...', type: 'info' };
      const powerAutomateUrl = 'https://defaultb81f0bdbd4f842fb9d75eec39739d0.78.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/0ff0a1421ade42f1b8e93ebaca32041c/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=pi-BSsCW1qGd_0_gw4QM_8v1A_pzkoGbRezteqrOXO0';
      try {
        const response = await fetch(powerAutomateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: this.form.name, email: this.form.email, phone: this.form.phone, message: this.form.message })
        });
        if (response.ok) {
          trackEvent('contact_form_submit', { status: 'success' });
          this.formStatus = { message: '¡Mensaje enviado! Gracias por contactarnos.', type: 'success' };
          this.form = { name: '', email: '', phone: '', message: '', captchaInput: '' };
        } else {
          trackEvent('contact_form_submit', { status: 'error' });
          this.formStatus = { message: 'Ocurrió un error al enviar. Intenta de nuevo más tarde.', type: 'error' };
        }
      } catch {
        trackEvent('contact_form_submit', { status: 'network_error' });
        this.formStatus = { message: 'Error de red. Revisa tu conexión e intenta de nuevo.', type: 'error' };
      }
      this.generateCaptcha();
      setTimeout(() => { this.formStatus = { message: '', type: '' }; }, 5000);
    },
    initParticles() {
      const canvas = this.$refs.heroCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const COUNT = 55;

      this.resizeParticles = () => {
        canvas.width  = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
      };
      this.resizeParticles();

      const rand = (a, b) => Math.random() * (b - a) + a;
      const particles = Array.from({ length: COUNT }, () => ({
        x:  rand(0, canvas.width),
        y:  rand(0, canvas.height),
        r:  rand(1.2, 3.2),
        vx: rand(-0.18, 0.18),
        vy: rand(-0.18, 0.18),
        o:  rand(0.06, 0.22),
      }));

      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#003b4c';
        for (const p of particles) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0) p.x = canvas.width;
          if (p.x > canvas.width)  p.x = 0;
          if (p.y < 0) p.y = canvas.height;
          if (p.y > canvas.height) p.y = 0;
          ctx.globalAlpha = p.o;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        this._particleRaf = requestAnimationFrame(draw);
      };
      draw();
    },
  },
  mounted() {
    this.generateCaptcha();
    if (this.$route.path === '/pago') this.showModal('PaymentInfo');
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.initParticles();
    window.addEventListener('resize', this.resizeParticles, { passive: true });
    this.startTallerAutoplay();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.resizeParticles);
    cancelAnimationFrame(this._particleRaf);
    this.stopTallerAutoplay();
  },
};
</script>

<style scoped>
.nav-ripple-btn {
  position: relative;
  overflow: hidden;
}

.ripple-wave {
  position: absolute;
  border-radius: 50%;
  background: rgba(237, 143, 123, 0.35);
  transform: scale(0);
  animation: ripple-expand 0.6s ease-out forwards;
  pointer-events: none;
}

@keyframes ripple-expand {
  to {
    transform: scale(1);
    opacity: 0;
  }
}
</style>
